import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { Button, Modal, Tag } from "antd";
import { toast } from "react-toastify";
import { fetchRcaTestSamples } from "../../../actions/qc";
import { formatAntdDateRange } from "../../../utils/helpers";
import { getDefaultLastOneWeekDateRange } from "./utils/utils";
import useAbortController from "../../Common/hooks/useAbortController";
import ReportDelayFilters from "../ReportDelay/ReportDelayFilters";
import ReportDelayTable from "../ReportDelay/ReportDelayTable";
import AddRcaSubmitModal from "./AddRcaSubmitModal";
import {
  buildAddRcaTestSamplesParams,
  extractRcaTestSamplesList,
  formatAddRcaTableRcaStatus,
  isAddRcaTestSampleExisting,
  mapRcaTestSampleRow,
} from "./utils/addRcaTestSamplesUtils";
import { formatRcaDisplayDate, getRcaStatusTagColor } from "./utils/rcaUtils";
import "../ReportDelay/ReportDelayFormPopup.css";

const defaultWODateRange = formatAntdDateRange(getDefaultLastOneWeekDateRange());

/** Fresh default Add RCA filters (last 1 week, no search/category). */
const createDefaultAddRcaFilters = () => {
  const dateRangeValues = getDefaultLastOneWeekDateRange();
  return {
    search: null,
    date_range: formatAntdDateRange(dateRangeValues),
    test_category_id: null,
  };
};

/** Keep only Add RCA filter fields from ReportDelayFilters API payload. */
const pickAddRcaFilterState = (apiParams = {}) => ({
  search: apiParams.search ?? null,
  date_range: apiParams.date_range ?? defaultWODateRange,
  test_category_id: apiParams.test_category_id ?? null,
});

const renderAddRcaStatusCell = (value, record) => {
  const label = formatAddRcaTableRcaStatus(value, record);
  if (label === "-") {
    return label;
  }
  return (
    <Tag color={getRcaStatusTagColor(value, record)}>
      {label}
    </Tag>
  );
};

const ADD_RCA_COLUMNS = [
  {
    title: "WO ID",
    dataIndex: "workorder_id",
    key: "workorder_id",
    onCell: () => ({ style: { minWidth: 110 } }),
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
    onCell: () => ({ style: { minWidth: 110 } }),
  },
  {
    title: "Customer Name",
    dataIndex: "customer_name",
    key: "customer_name",
    ellipsis: true,
    onCell: () => ({ style: { minWidth: 150 } }),
  },
  {
    title: "Sample ID",
    dataIndex: "sample_id",
    key: "sample_id",
    onCell: () => ({ style: { minWidth: 100 } }),
    render: (value) => (value != null && String(value).trim() !== "" ? value : "-"),
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
    onCell: () => ({ style: { minWidth: 120 } }),
  },
  {
    title: "Test Category",
    dataIndex: "test_category",
    key: "test_category",
    onCell: () => ({ style: { minWidth: 120 } }),
  },
  {
    title: "Expected",
    dataIndex: "expected_complete_date",
    key: "expected_complete_date",
    onCell: () => ({ style: { minWidth: 110 } }),
    render: (value) => formatRcaDisplayDate(value),
  },
  {
    title: "RCA Status",
    dataIndex: "rca_status",
    key: "rca_status",
    fixed: "right",
    width: 120,
    onCell: () => ({ style: { minWidth: 120 } }),
    render: renderAddRcaStatusCell,
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 96,
    align: "center",
    className: "ReportDelayFormPopup__ActionsColumn",
  },
];

/**
 * Add RCA — eligible tests from GET rca_test_samples/ (test_in_samples source with 1 week date range).
 */
export default function AddRcaFormPopup({ open, onCancel }) {
  const [filters, setFilters] = useState(createDefaultAddRcaFilters);
  const [filterFormKey, setFilterFormKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [submitRow, setSubmitRow] = useState(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [shouldRefreshParent, setShouldRefreshParent] = useState(false);
  const { setNewAbortController, clearAbortController, abortController } = useAbortController();
  const skipFilterFetchOnOpenRef = useRef(false);

  const searchEligibleRows = async (params) => {
    const newAbortController = setNewAbortController();
    setResults([]);
    setLoading(true);
    try {
      const response = await fetchRcaTestSamples(params, newAbortController.signal);
      if (newAbortController !== abortController.current) {
        return;
      }
      if (response.success) {
        const rawResults = extractRcaTestSamplesList(response);
        setResults(rawResults.map(mapRcaTestSampleRow));
      } else if (!response.canceled) {
        toast.error(response.message || "Failed to fetch eligible tests");
        setResults([]);
      }
    } finally {
      const isLatest = newAbortController === abortController.current;
      if (isLatest) {
        setHasSearched(true);
        clearAbortController();
        setLoading(false);
      }
    }
  };

  // Every Add RCA open: defaults only + remount filter UI; fetch with those defaults.
  useEffect(() => {
    if (!open) {
      skipFilterFetchOnOpenRef.current = false;
      return;
    }
    const defaultFilters = createDefaultAddRcaFilters();
    skipFilterFetchOnOpenRef.current = true;
    setFilters(defaultFilters);
    setFilterFormKey((key) => key + 1);
    setResults([]);
    setHasSearched(false);
    setSubmitRow(null);
    setShouldRefreshParent(false);
    searchEligibleRows(buildAddRcaTestSamplesParams(defaultFilters, defaultFilters.date_range));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // User-driven filter changes while open (skip the setFilters from the open reset).
  useEffect(() => {
    if (!open) {
      return;
    }
    if (skipFilterFetchOnOpenRef.current) {
      skipFilterFetchOnOpenRef.current = false;
      return;
    }
    searchEligibleRows(buildAddRcaTestSamplesParams(filters, defaultWODateRange));
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (onCancel) {
      onCancel(shouldRefreshParent);
    }
  };

  const handleFiltersChange = useCallback((nextFilters) => {
    setFilters(pickAddRcaFilterState(nextFilters));
  }, []);

  const handleActionRow = useCallback((record) => {
    setSubmitRow(record);
    setIsSubmitModalOpen(true);
  }, []);

  const tableColumns = ADD_RCA_COLUMNS.map((column) => {
    if (column.key !== "actions") {
      const baseOnCell = column.onCell;
      return {
        ...column,
        onCell: (...args) => {
          const baseCell = baseOnCell ? baseOnCell(...args) : {};
          return {
            ...baseCell,
            style: { ...(baseCell.style || {}), cursor: "default" },
          };
        },
      };
    }
    return {
      ...column,
      onHeaderCell: () => ({ className: "ReportDelayFormPopup__ActionsColumn" }),
      onCell: () => ({ className: "ReportDelayFormPopup__ActionsColumn" }),
      render: (_value, record) => {
        const isEdit = isAddRcaTestSampleExisting(record);
        return (
          <Button
            type="link"
            size="small"
            className="ReportDelayFormPopup__RowEditAction"
            onClick={(event) => {
              event.stopPropagation();
              handleActionRow(record);
            }}
          >
            {isEdit ? "Edit" : "Add"}
          </Button>
        );
      },
    };
  });

  const closeSubmitModal = (submitted) => {
    setIsSubmitModalOpen(false);
    setSubmitRow(null);
    if (submitted) {
      setShouldRefreshParent(true);
      searchEligibleRows(buildAddRcaTestSamplesParams(filters, defaultWODateRange));
    }
  };

  return (
    <>
      <Modal
        centered
        title="Add RCA"
        width={1500}
        open={open}
        onCancel={handleClose}
        rootClassName="ReportDelayFormPopup"
        footer={null}
        destroyOnClose
      >
        <div className="ReportDelayFormPopup__BodyContent">
          <div className="ReportDelayFormPopup__Section">
            <ReportDelayFilters
              key={filterFormKey}
              onFiltersChange={handleFiltersChange}
              initialFilters={{
                search: "",
                dateRange: getDefaultLastOneWeekDateRange(),
                selectedCategory: "",
              }}
              filterVisibility={{
                reportedBy: false,
                reason: false,
                status: false,
              }}
              enableInitialInputFocus
            />

            <ReportDelayTable
              className="ReportDelayTable ReportDelayTable--AddRca"
              rowKey={(row) => row.tis_pk ?? row.tests_in_sample_pk ?? row.key}
              dataSource={results}
              pagination
              columns={tableColumns}
              loading={loading}
              scroll={{ x: "max-content", y: 1 }}
              emptyText={hasSearched ? "No Data" : "Loading eligible tests..."}
              onRow={() => ({ style: { cursor: "default" } })}
            />
          </div>
        </div>

        <div className="ReportDelayFormPopup__Footer">
          <Button onClick={handleClose}>
            Close
          </Button>
        </div>
      </Modal>

      <AddRcaSubmitModal
        open={isSubmitModalOpen}
        selectedRows={submitRow ? [submitRow] : []}
        onCancel={() => closeSubmitModal(false)}
        onSubmitted={() => closeSubmitModal(true)}
      />
    </>
  );
}
